<template>
  <div v-if="lesson" class="page stack">
    <p class="eyebrow">Lesson {{ lesson.number }} · {{ lesson.dish }}</p>

    <!-- 2026-10-27: a failed submit printed one line under the buttons,
         behind the iOS keyboard, so it looked like nothing happened. The
         error sits up here now, the keyboard is dropped on submit, and the
         page scrolls to it. -->
    <div v-if="error" ref="errorBox" class="debrief-error" role="alert">
      <strong>That didn’t go through.</strong> {{ error }} Everything you said is still in the box.
    </div>

    <template v-if="!summary">
      <h1>What did everyone think?</h1>
      <p class="muted">Tap the mic on the keyboard and let each person say their piece, one after the other. Names help (“Carrie thought…”). Nothing is too small: the sauce, the timing, whether it was worth it.</p>
      <p v-if="lesson.debriefPrompt" class="prompt">{{ lesson.debriefPrompt }}</p>
      <label for="debrief-input" class="talk-label">What everyone said</label>
      <textarea id="debrief-input" v-model="transcript" class="talk" name="debrief" autocomplete="off" autocorrect="on" autocapitalize="sentences" spellcheck="true" rows="9" placeholder="Matt: honestly better than I expected, the crust was…" :disabled="busy"></textarea>
      <div class="row">
        <button type="button" class="btn btn-primary btn-lg" :disabled="busy || transcript.trim().length < 10" @click="submit">{{ busy ? 'Listening…' : 'Done talking' }}</button>
        <router-link to="/" class="btn">Later</router-link>
      </div>
      <p class="muted small">Saved as you type. The summary goes into the course; the transcript stays with it.</p>
    </template>

    <template v-else>
      <p v-if="justSaved" class="saved" role="status">✓ Saved to the course</p>
      <h1>{{ summary.verdict }}</h1>
      <section v-if="summary.people?.length" class="card">
        <p class="eyebrow">Who said what</p>
        <ul class="plain-list">
          <li v-for="p in summary.people" :key="p.name"><strong>{{ p.name }}:</strong> {{ p.take }}</li>
        </ul>
      </section>
      <section v-if="summary.worked?.length" class="card">
        <p class="eyebrow">What worked</p>
        <ul class="plain-list bullets"><li v-for="(w, i) in summary.worked" :key="i">{{ w }}</li></ul>
      </section>
      <section v-if="summary.change?.length" class="card">
        <p class="eyebrow">What to change</p>
        <ul class="plain-list bullets"><li v-for="(c, i) in summary.change" :key="i">{{ c }}</li></ul>
      </section>
      <section v-if="summary.nextTime" class="card">
        <p class="eyebrow">For the next lesson</p>
        <p>{{ summary.nextTime }}</p>
      </section>
      <p class="muted small">Worth it: {{ summary.worthIt }} of 5 · {{ summary.again ? 'Would make it again' : 'Not asking for it again' }}</p>
      <router-link to="/" class="btn btn-primary btn-lg">Back to the course</router-link>
    </template>
  </div>
</template>

<script>
// One button after dinner. Everyone talks; the app captures, summarises,
// and the next lesson reads the summary. A rating would have been a
// number nobody argues with; a conversation is the actual review.
import { computed, nextTick, ref, watch } from 'vue';
import { lessonById, LESSONS } from '../curriculum/lessons.js';
import { readDraft, writeDraft, clearDraft } from '../utils/talkDraft.js';
import { summarizeDebrief } from '../utils/api.js';
import { loadProgress, markLesson, debriefHistory } from '../utils/course.js';
import { dbGet, dbPut } from '../store/db.js';
import { notesPath, chatPath, cleanTurns, MAX_CHAT_TURNS } from '../utils/teacher.js';
import { setWhere, setActivity } from '../state.js';

export default {
  name: 'DebriefScreen',
  props: { id: { type: String, required: true }, hat: { type: String, default: null } },
  setup (props) {
    const lesson = computed(() => lessonById(props.id));
    const transcript = ref(readDraft(props.id));
    const summary = ref(null);
    const busy = ref(false);
    const error = ref(null);
    const errorBox = ref(null);
    const justSaved = ref(false);
    const progress = ref({});
    // A summary that came back but could not be saved, kept so a retry saves
    // it rather than asking for (and paying for) a second one.
    let unsaved = null;
    setActivity('debrief');
    setWhere({ screen: 'debrief', lessonId: props.id, phase: null, stepIndex: 0 });

    watch(transcript, (text) => writeDraft(props.id, text));
    watch(() => props.hat, async (hat) => {
      progress.value = hat ? await loadProgress(hat) : {};
      const existing = progress.value[props.id]?.debrief?.summary;
      if (existing) summary.value = existing;
    }, { immediate: true });

    async function submit () {
      // Drop the keyboard first: on a phone it covers the half of the page
      // where anything we say about the submit would appear.
      if (document.activeElement && typeof document.activeElement.blur === 'function') document.activeElement.blur();
      busy.value = true;
      error.value = null;
      const text = transcript.value;
      try {
        let result = unsaved?.text === text ? unsaved.result : null;
        if (!result) {
          const previous = debriefHistory(progress.value, LESSONS).filter((d) => d.lessonId !== props.id).slice(-3);
          // The teacher's notes go in and come back rewritten with tonight in
          // them: this is how the debrief becomes memory (utils/teacher.js).
          const notesBefore = (await dbGet(notesPath(props.hat)).catch(() => '')) || '';
          try {
            result = await summarizeDebrief({ lesson: lesson.value, transcript: text, previous, notes: typeof notesBefore === 'string' ? notesBefore : '' });
          } catch (err) {
            throw new Error(friendly(err));
          }
          unsaved = { text, result };
        }
        // Saved BEFORE it is shown: a summary on screen must mean a summary
        // in the course. It used to show first, and a failed save looked done.
        const fields = { debrief: { transcript: text, summary: result, at: Date.now() } };
        if (!progress.value[props.id]?.cookedAt) fields.cookedAt = Date.now();
        try {
          await markLesson(props.hat, props.id, fields);
        } catch {
          throw new Error('The summary came back but could not be saved to the course. Check the signal and tap Done talking again.');
        }
        unsaved = null;
        clearDraft(props.id);
        summary.value = result;
        justSaved.value = true;
        if (result.notes) {
          await dbPut(notesPath(props.hat), result.notes).catch(() => {});
          const turns = cleanTurns((await dbGet(chatPath(props.hat)).catch(() => [])) || []);
          turns.push({ role: 'did', text: 'Updated my notes about you from tonight\'s debrief.', at: Date.now(), where: { screen: 'debrief', lessonId: props.id, label: 'debrief' } });
          await dbPut(chatPath(props.hat), turns.slice(-MAX_CHAT_TURNS)).catch(() => {});
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (err) {
        error.value = err.message;
        await nextTick();
        errorBox.value?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } finally {
        busy.value = false;
      }
    }

    return { lesson, transcript, summary, busy, error, errorBox, justSaved, submit };
  }
};

// What the household sees when the summary call fails. The raw messages are
// "Load failed" (no signal) or "responded 503" (the gateway gave up at 30 s).
function friendly (err) {
  const message = String(err?.message || '');
  if (err instanceof TypeError || /load failed|failed to fetch|network/i.test(message)) return 'No connection reached the kitchen helper.';
  if (/\b50[34]\b/.test(message)) return 'The kitchen helper took too long to answer. Try again in a moment.';
  return message || 'The kitchen helper had a problem.';
}
</script>

<style scoped>
.talk-label { display: block; font-size: 0.8rem; color: var(--muted); margin-bottom: 0.3rem; }
.prompt { font-style: italic; color: var(--muted); }
.debrief-error { border: 1px solid var(--danger); background: #fbeaea; color: var(--danger); border-radius: 10px; padding: 0.75rem 0.9rem; }
.saved { margin: 0; font-weight: 600; color: #1d6b3a; }
.small { font-size: 0.85rem; }
.plain-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.4rem; }
.plain-list.bullets { list-style: disc; padding-left: 1.2rem; }
</style>
